import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmButton,
  CmCard,
  CmCheckBox,
  CmFlex,
  CmFormErrorMessage,
  CmFormLabel,
  CmHeading,
  CmLabelAndInput,
  CmLabelAndSelect,
  CmLongLoading,
  CmMessageCard,
  CmRadio,
  CmRadioGroup,
  CmScrollBox,
  CmVStack,
} from '@/components/uiParts';
import { redirect404Page } from '@/lib/AccessControl';
import { BaseDeliveryFeeAndAdditionalDistanceFeeForm } from '@/pages/camel-order/delivery/components/Form/BaseDeliveryFeeAndAdditionalDistanceFeeForm';
import { DeliveryFormInput } from '@/pages/camel-order/delivery/components/Form/DeliveryFormInput';
import { useCamelOrderDeliveryForm } from '@/pages/camel-order/delivery/hooks/useCamelOrderDeliveryForm';
import {
  camelOrderDeliveryForm,
  DELIVERY_FEE_TYPES,
  deliveryFeeTypeMap,
  PREFERENCE_TYPES,
} from '@/pages/camel-order/delivery/service';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';

const CamelOrderDeliveryPage: React.FC = () => {
  // /* アクセス制御 */
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  redirect404Page('camel_order_site_setting', history, managerAccountMe);

  const {
    sites,
    isLoading,
    siteIndex,
    error,
    handleChangeSiteUrl,
    handleChangeDeliveryFeeType,
    handleChangeBaseDeliveryFee,
    handleChangeBaseDeliveryFeeDistance,
    handleChangeAdditionalDistanceFee,
    handleChangeAdditionalDistance,
    handleChangeFixedDiscountAmount,
    handleChangePreferenceType,
    handleChangeDeliveryProviderChecked,
    handleChangeUberDirectCustomerId,
    initialize,
    handleUpdate,
  } = useCamelOrderDeliveryForm();

  const targetSite = sites[siteIndex];

  useEffect(() => {
    (async () => initialize())();
  }, []);

  const { baseDeliveryFee, fixedDiscountAmount, uberDirectCustomerId } =
    camelOrderDeliveryForm;

  const isDisabledUpdateButton = isLoading;

  return (
    <PageLayout title={routes.camelOrderDelivery.title}>
      <div>
        {sites.length > 0 ? (
          <CmCard
            mt="25px"
            height={`calc(100vh - 120px)`}
            padding={'20px 40px 10px'}
          >
            <CmFlex justify="space-between">
              <CmFlex alignItems="center" p="0 0 10px">
                <CmLabelAndSelect
                  id={'siteUrl'}
                  label={'サイトURLを選択'}
                  width="300px"
                  fontWeight="bold"
                  value={siteIndex}
                  onChange={handleChangeSiteUrl}
                >
                  {sites.map((site, index) => (
                    <option key={site.site_url} value={index}>
                      {site.site_url}
                    </option>
                  ))}
                </CmLabelAndSelect>
              </CmFlex>
              {/*
               // @ts-ignore TS2590 */}
              <CmButton
                isDisabled={isDisabledUpdateButton}
                onClick={handleUpdate}
              >
                更新
              </CmButton>
            </CmFlex>
            <CmScrollBox>
              <CmBox mt="20px">
                <CmHeading fontSize="18px" pb="10px" isRequired>
                  配達料設定（配達代行）
                </CmHeading>
                <CmBox>
                  <CmRadioGroup
                    value={targetSite.delivery.delivery_fee_type}
                    onChange={handleChangeDeliveryFeeType}
                  >
                    <CmVStack direction="row" align="stretch">
                      {DELIVERY_FEE_TYPES.filter(
                        // 基本配送料＋追加距離料金は当初対応しないため非表示にする
                        (deliveryFeeType) =>
                          deliveryFeeType.label !==
                          deliveryFeeTypeMap.BASE_DELIVERY_FEE_AND_ADDITIONAL_DISTANCE_FEE
                      ).map((type) => (
                        <CmRadio
                          key={type.value}
                          size="md"
                          value={type.value}
                          id={type.value}
                        >
                          {type.label}
                        </CmRadio>
                      ))}
                    </CmVStack>
                    <CmBox mt="20px">
                      {
                        {
                          DELIVERY_PROVIDER_FEE: <></>,
                          DELIVERY_PROVIDER_FEE_WITH_DISCOUNT: (
                            <DeliveryFormInput
                              {...{
                                error: error.fixedDiscountAmount,
                                handleChangeDeliveryFormInput:
                                  handleChangeFixedDiscountAmount,
                                value:
                                  targetSite.delivery.fixed_discount_amount,
                                suffix: '円',
                                label: (
                                  <CmFormLabel
                                    htmlFor={fixedDiscountAmount.key}
                                  >
                                    {fixedDiscountAmount.label}
                                  </CmFormLabel>
                                ),
                                placeholder: '500',
                              }}
                            />
                          ),
                          BASE_DELIVERY_FEE: (
                            <DeliveryFormInput
                              {...{
                                error: error.baseDeliveryFee,
                                handleChangeDeliveryFormInput:
                                  handleChangeBaseDeliveryFee,
                                value: targetSite.delivery.base_delivery_fee,
                                suffix: '円',
                                label: (
                                  <CmFormLabel htmlFor={baseDeliveryFee.key}>
                                    {baseDeliveryFee.label}
                                  </CmFormLabel>
                                ),
                                placeholder: '500',
                              }}
                            />
                          ),
                          BASE_DELIVERY_FEE_AND_ADDITIONAL_DISTANCE_FEE: (
                            <BaseDeliveryFeeAndAdditionalDistanceFeeForm
                              {...{
                                error,
                                base_delivery_fee:
                                  targetSite.delivery.base_delivery_fee,
                                base_delivery_fee_distance:
                                  targetSite.delivery
                                    .base_delivery_fee_distance,
                                additional_distance_fee:
                                  targetSite.delivery.additional_distance_fee,
                                additional_distance:
                                  targetSite.delivery.additional_distance,
                                handleChangeBaseDeliveryFee,
                                handleChangeBaseDeliveryFeeDistance,
                                handleChangeAdditionalDistanceFee,
                                handleChangeAdditionalDistance,
                                handleChangeFixedDiscountAmount,
                              }}
                            />
                          ),
                        }[targetSite.delivery.delivery_fee_type]
                      }
                    </CmBox>
                  </CmRadioGroup>
                </CmBox>

                <CmHeading fontSize="18px" pb="10px" isRequired>
                  配送事業者の選択方法
                </CmHeading>
                <CmBox>
                  <CmRadioGroup
                    value={targetSite.delivery.preference_type}
                    onChange={handleChangePreferenceType}
                  >
                    <CmVStack direction="row" align="stretch">
                      {PREFERENCE_TYPES.map((type) => (
                        <CmRadio
                          key={type.value}
                          size="md"
                          value={type.value}
                          id={type.value}
                        >
                          {type.label}
                        </CmRadio>
                      ))}
                    </CmVStack>
                  </CmRadioGroup>
                </CmBox>

                <CmBox mt="30px">
                  <CmHeading fontSize="18px" isRequired>
                    配達事業者設定
                  </CmHeading>
                  <CmBox mt="10px">
                    <CmHeading fontSize="18px" pb="10px">
                      Uber Direct
                    </CmHeading>
                    <CmFlex alignItems={'center'}>
                      <CmCheckBox
                        size="md"
                        isChecked={targetSite.delivery.delivery_providers.includes(
                          'UBER_DIRECT'
                        )}
                        onChange={handleChangeDeliveryProviderChecked}
                        value="UBER_DIRECT"
                      />
                      <CmBox ml="10px">
                        <CmLabelAndInput
                          isRequired
                          label={uberDirectCustomerId.label}
                          id={uberDirectCustomerId.key}
                          value={targetSite.delivery.uber_direct_customer_id}
                          isInvalid={error.uberDirectCustomerId.isError}
                          onChange={handleChangeUberDirectCustomerId}
                        >
                          <CmFormErrorMessage>
                            {error.uberDirectCustomerId.message}
                          </CmFormErrorMessage>
                        </CmLabelAndInput>
                      </CmBox>
                    </CmFlex>
                  </CmBox>
                </CmBox>
              </CmBox>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                サイト情報が登録されていません。
              </CmMessageCard>
            )}
          </>
        )}
      </div>
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default CamelOrderDeliveryPage;
